import { Button, FormSwitch, SkeletonSpinnerCentered } from '@civilgrid/ui/components';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useSnapshot } from 'valtio';
import { updateProjectMemberNotifications as apiUpdatePrefs } from '~/api/preferences';
import { getProject as apiGetProject } from '~/api/projects2';
import { captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { Member } from '~/common/types';
import EmailLink from '~/components2/EmailLink';
import Form from '~/components2/forms/Form';
import { Notify } from '~/components2/Notify';
import rootStore from '~/store';

type TFormValues = Member['notifications']['civilsearch'];

// placeholder to make react-hook-form happy
const defaultValues: TFormValues = {
  projectWeeklyDigest: false,
  newRequestsCreated: false,
  newMessagesCreated: false,
  requestStatusUpdates: false,
  requestDocumentsAdded: false,
  requestCompleteOnly: false,
};

export function TabDetails() {
  const [isMember, setIsMember] = useState(false);
  const { projectId } = useParams({ strict: false }) as { projectId: string };
  const { me } = useSnapshot(rootStore) as typeof rootStore;
  const form = useForm<TFormValues>({ defaultValues });

  const requestCompleteOnly = form.watch('requestCompleteOnly');
  const requestStatusUpdates = form.watch('requestStatusUpdates');

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (requestStatusUpdates) {
      form.setValue('requestCompleteOnly', false);
    }
  }, [requestStatusUpdates]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (requestCompleteOnly) {
      form.setValue('requestStatusUpdates', false);
    }
  }, [requestCompleteOnly]);

  const query = useQuery({
    staleTime: 0,
    gcTime: 0,
    enabled: !!projectId,
    queryKey: ['user-project-preferences', 'notifications-civilsearch', { projectId }],
    queryFn: async () => {
      const res = await apiGetProject(projectId);
      const member = res.members.find((m) => m.user.id === me?.id);
      if (member) {
        form.reset(member.notifications.civilsearch);
        setIsMember(true);
      }
      return res;
    },
  });

  const mutation = useMutation({
    mutationKey: ['user-project-preferences', 'notifications-civilsearch', { projectId }],
    mutationFn: async (updates: Partial<Member['notifications']>) => apiUpdatePrefs(projectId, updates),
    onSuccess: (data) => {
      const prefs = data.notifications.civilsearch;
      form.reset(prefs);
      captureTypedPosthogEvent(POSTHOG_EVENTS.utilitiesNotificationPreferencesUpdated, {
        project_id: projectId,
        project_weekly_digest: prefs.projectWeeklyDigest,
        new_requests_created: prefs.newRequestsCreated,
        new_messages_created: prefs.newMessagesCreated,
        request_documents_added: prefs.requestDocumentsAdded,
        request_status_updates: prefs.requestStatusUpdates,
        request_complete_only: prefs.requestCompleteOnly,
      });
      Notify.info({
        title: 'Notification Preferences',
        message: 'Your notification preferences have been updated for this project',
      });
    },
  });

  const onSubmit: SubmitHandler<TFormValues> = async (data) => {
    mutation.mutate({ civilsearch: data });
  };

  const isLoading = query.isFetching;
  const isProcessing = mutation.isPending;
  const isError = query.isError || mutation.isError;
  const isDisabled = !form.formState.isDirty || isLoading || isProcessing || isError;

  if (isLoading) return <SkeletonSpinnerCentered />;
  if (isError) {
    return (
      <div>
        An error occurred while {mutation.isError ? 'updating' : 'fetching'} your notification preferences, please{' '}
        <EmailLink>contact support</EmailLink>.
      </div>
    );
  }

  if (!isMember) {
    return (
      <div>
        You are not a member of this project. To receive notifications, join the{' '}
        <Link
          to="/projects/$projectId/$tabId"
          params={{ projectId, tabId: 'team' }}
          className="text-blue-50 underline hover:text-blue-60"
        >
          project
        </Link>
        .
      </div>
    );
  }

  function renderItem(name: keyof TFormValues, title: string, description: string) {
    return (
      <FormSwitch
        control={form.control}
        name={name}
        label={<span className="text-base font-medium text-secondary">{title}</span>}
        description={<span className="text-sm text-primary">{description}</span>}
        disabled={isProcessing}
        data-testid={`toggle-utilities-${name}`}
      />
    );
  }

  return (
    <Form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
      {renderItem('projectWeeklyDigest', 'Project Weekly Digest', 'Sent every Tuesday morning')}

      {renderItem('newRequestsCreated', 'New Requests', 'When new requests are created for this project')}

      {renderItem('newMessagesCreated', 'New CivilSearch Messages', 'Every new messages in the Project Channel')}

      {renderItem(
        'requestDocumentsAdded',
        'Request Documents Added',
        'When documents have been added to a data request',
      )}

      {renderItem('requestStatusUpdates', 'Request Status Updates', 'Every status update (batched every 2 hours)')}

      {renderItem('requestCompleteOnly', 'Request Complete Only', 'Only when a data request is complete')}

      <Button
        type="submit"
        disabled={isDisabled}
        loading={isProcessing}
        data-testid="button-save-utilities-notifications"
      >
        Save Preferences
      </Button>
      <div className="text-sm">
        <b>Note:</b> These preferences apply only to this project.
      </div>
    </Form>
  );
}
